import { useCallback, useState } from 'react';
import {
  buildGuaranteesApplicationVersionMismatchModal,
  getGuaranteesErrorMessage,
  isGuaranteesApplicationVersionMismatch,
} from '~/modules/guarantees/utils.ts';
import { fetchGuaranteesApplication } from '~/store/guarantees/thunks/applicationThunks.ts';
import type { AppDispatch } from '~/store/store.ts';
import type { TGuaranteesModalAlertState, TGuaranteesModalSuccessState } from '../../types.ts';

type TUseApplicationRequestFeedbackParams = {
  applicationId: string;
  contragentId: string;
  dispatch: AppDispatch;
};

export const useApplicationRequestFeedback = ({
  applicationId,
  contragentId,
  dispatch,
}: TUseApplicationRequestFeedbackParams) => {
  const [modalAlert, setModalAlert] = useState<TGuaranteesModalAlertState>({ text: '' });
  const [modalSuccess, setModalSuccess] = useState<TGuaranteesModalSuccessState>({ text: '' });

  const handleRefreshApplication = useCallback(() => {
    setModalAlert({ text: '' });

    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
  }, [applicationId, contragentId, dispatch]);

  const showGuaranteesRequestError = useCallback(
    (error: unknown) => {
      if (isGuaranteesApplicationVersionMismatch(error)) {
        setModalAlert(buildGuaranteesApplicationVersionMismatchModal(handleRefreshApplication));

        return;
      }

      setModalAlert({ text: getGuaranteesErrorMessage(error) });
    },
    [handleRefreshApplication]
  );

  const handleCloseAlertModal = () => {
    const onClose = modalAlert.onClose;

    setModalAlert({ text: '' });
    onClose?.();
  };

  const handleCloseSuccessModal = () => {
    const onClose = modalSuccess.onClose;

    setModalSuccess({ text: '' });
    onClose?.();
  };

  return {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  };
};
